import {
  MultiTurnInteractionConfiguration,
  IndexedInteractionStepResponse,
} from '@scale/shared/types/chat';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { Typography, Banner, SelectMenu, Tab, Button, Divider } from '@scale/scaleui';
import {
  ReviewLevel,
  parseStringAsReviewLevel,
  GENAI_BENCHMARK_CENTER_REVIEW_LEVEL_OPTIONS,
} from '@scale/shared/defaults';
import {
  useMultiTurnInteractionValidation,
  getForcedStaticStepParams,
  syncBenchmarkResponses,
  DEBOUNCE_MILLISECONDS,
} from '../utils';
import { withErrorBoundary } from 'react-scale/containers/global/errorBoundary';
import { validateChatResponse } from '@scale/shared/chat/response';
import { useChatLayoutStore, ChatLayout } from '../hooks/useChatLayoutStore';
import { useCreateChatStore } from '../../Chat/hooks/ChatStore';
import { useStore } from 'zustand';
import { useInlineResponseTemplatingContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/InlineResponseTemplatingContext';
import { useDebounce } from 'react-use-v13';
import {
  ChatInteractionWithErrorBoundary,
  ResponseTemplateCreatorProps,
} from './ResponseTemplateCreatorOriginal';
import _ from 'lodash';
import copy from 'copy-to-clipboard';

interface ResponseTemplateCreatorInlineProps extends ResponseTemplateCreatorProps {
  // Callback to sync templated responses upstream
  syncInitialResponses?: (inferredInitialResponses: IndexedInteractionStepResponse[]) => void;
  // If provided, changes to templateResponses will be propagated using these responses as the base
  initialTemplateResponses?: IndexedInteractionStepResponse[];
}

function ResponseTemplateCreator({
  config: _config,
  context,
  initialTemplateResponses,
  resetTemplateResponses,
  populateResponses,
  templateVariables,
  previewReviewLevel: _previewReviewLevel,
  previewReadOnly = true,
  syncInitialResponses,
}: ResponseTemplateCreatorInlineProps) {
  const { setLayout } = useChatLayoutStore();

  const { history, templatedResponses, setTemplatedResponses, setVariables, clear } =
    useInlineResponseTemplatingContext();

  const [originalResponses] = useState<IndexedInteractionStepResponse[] | null>(
    _.cloneDeep(templatedResponses),
  );

  const config: MultiTurnInteractionConfiguration = useMemo(() => {
    return {
      ..._config,
      before: _config.before.map(p => {
        return getForcedStaticStepParams(p, true);
      }),
      turn: _config.turn.map(p => {
        return getForcedStaticStepParams(p, true);
      }),
      after: _config.after.map(p => {
        return getForcedStaticStepParams(p, true);
      }),
    };
  }, [_config]);

  const previewChatStore = useCreateChatStore({
    config,
    responses: _.cloneDeep(templatedResponses ?? []),
    reviewLevel: _previewReviewLevel,
  });
  const previewConfig = useStore(previewChatStore.current, state => state.config);
  const setPreviewConfig = useStore(previewChatStore.current, state => state.setConfig);
  const previewResponses = useStore(previewChatStore.current, state => state.responses);
  const _setPreviewResponses = useStore(previewChatStore.current, state => state.setResponses);
  const setPreviewResponses = useCallback(
    async (responses: IndexedInteractionStepResponse[] | null) => {
      if (!responses) {
        _setPreviewResponses([]);
        return;
      }
      if (populateResponses) {
        const populated = await populateResponses(responses);
        _setPreviewResponses(populated);
      } else {
        _setPreviewResponses(responses);
      }
    },
    [_setPreviewResponses, populateResponses],
  );
  const previewReviewLevel = useStore(previewChatStore.current, state => state.reviewLevel);
  const setPreviewReviewLevel = useStore(previewChatStore.current, state => state.setReviewLevel);

  const [activePreviewTab, setActivePreviewTab] = useState<number>(0);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyToClipboard = useCallback(() => {
    copy(JSON.stringify(templatedResponses, null, 2));
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  }, [templatedResponses]);

  const maybeFallbackToResetResponses = () => {
    if (!originalResponses && resetTemplateResponses) {
      setTemplatedResponses(_.cloneDeep(resetTemplateResponses));
      setPreviewResponses(_.cloneDeep(resetTemplateResponses));
    }
  };

  // This debounce is a hack since resetTemplateResponse is iteratively generated.
  // If resetTemplateResponse was explicitly set once, we could just rely on the call in the initial effect
  useDebounce(
    () => {
      maybeFallbackToResetResponses();
    },
    DEBOUNCE_MILLISECONDS,
    [originalResponses, resetTemplateResponses],
  );

  useEffect(() => {
    setVariables(templateVariables ?? []);
  }, [templateVariables]);

  // Sync preview responses to templated responses, maybe switch this to require explicit user action if we use this for editing
  useDebounce(
    () => {
      setPreviewResponses(_.cloneDeep(templatedResponses));
    },
    DEBOUNCE_MILLISECONDS,
    [templatedResponses, templateVariables],
  );

  useEffect(() => {
    setPreviewResponses(_.cloneDeep(templatedResponses));
    maybeFallbackToResetResponses();
    // NOTE: Use only once when component is mounted
    setLayout(ChatLayout.Inline);
    return () => {
      clear();
    };
  }, []);

  useEffect(() => {
    setPreviewConfig(config);
  }, [config, setPreviewConfig]);

  useEffect(() => {
    setPreviewReviewLevel(_previewReviewLevel);
  }, [_previewReviewLevel, setPreviewReviewLevel]);

  const { canSubmit, submitTooltip } = useMultiTurnInteractionValidation({
    config: previewConfig,
    responses: previewResponses,
    isSubmitting: false,
    isLoading: false,
    hasResponseLoadingError: false,
    workerComments: [],
    reviewLevel: previewReviewLevel,
    allStepsCompleted: true,
  });

  const { valid, message } = useMemo(() => {
    if (previewResponses && canSubmit) {
      try {
        return validateChatResponse(previewConfig, previewResponses, {
          includePerResponseValidation: true,
        });
      } catch (e) {
        return { valid: false, message: e.message };
      }
    } else return { valid: true, message: '' };
  }, [canSubmit, previewConfig, previewResponses]);

  const syncResponses = useCallback(() => {
    if (history && initialTemplateResponses) {
      const { valid, inferredResponses } = syncBenchmarkResponses(
        history,
        config,
        initialTemplateResponses,
      );
      // Only sync the inferred initial responses if they are valid
      if (valid && inferredResponses) {
        syncInitialResponses?.(inferredResponses);
      }
    }
  }, [config, history, initialTemplateResponses, syncInitialResponses]);

  // Sync initial responses upstream, maybe switch this to require explicit user action if we use this for editing
  useDebounce(
    () => {
      if (initialTemplateResponses && syncInitialResponses && canSubmit && valid) {
        syncResponses();
      }
    },
    DEBOUNCE_MILLISECONDS,
    [templatedResponses],
  );

  return (
    <div className="flex-1 flex flex-col min-w-[1080px] gap-4">
      {!(canSubmit && valid) && (
        <Banner title="Validation Warning:" subtitle={message || submitTooltip} variant="warning" />
      )}
      <div className="flex flex-row">
        <SelectMenu
          options={GENAI_BENCHMARK_CENTER_REVIEW_LEVEL_OPTIONS}
          selectedOptionId={previewReviewLevel?.toString()}
          setSelected={(id: string) => {
            setPreviewReviewLevel(parseStringAsReviewLevel(id) as ReviewLevel);
          }}
          label={'Preview Review Level'}
        />
      </div>
      <Tab.Group selectedIndex={activePreviewTab} onChange={setActivePreviewTab}>
        <Tab.List>
          <Tab>Task Preview</Tab>
          <Tab>JSON</Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <div className="flex w-full justify-center my-4 bg-neutral-0">
              <ChatInteractionWithErrorBoundary
                chatStore={previewChatStore}
                context={{
                  ...context,
                  // TODO: technically should check if it's the first review level in project, fix once we pass project pipeline to this component
                  isInitialAttempt: previewReviewLevel === ReviewLevel.Attempt,
                  responseTemplatingMode: true,
                }}
                readOnly={previewReadOnly}
              />
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <div className="border border-neutral-300 bg-neutral-0 border-1 rounded-md">
              <div className="flex justify-between items-center p-4">
                <Typography.Subtitle3>Response JSON</Typography.Subtitle3>
                <Button variant="outline" onClick={handleCopyToClipboard}>
                  {isCopied ? 'Copied!' : 'Copy JSON to Clipboard'}
                </Button>
              </div>
              <Divider />
              <div className="p-4 whitespace-pre overflow-x-auto font-mono">
                {JSON.stringify(templatedResponses, null, 2)}
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}

function ErrorPlaceholderComponent() {
  return <Typography.Subtitle2>Something went wrong.</Typography.Subtitle2>;
}

export default withErrorBoundary({ reloadable: true, ErrorPlaceholderComponent })(
  ResponseTemplateCreator,
);
